import React, { useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Download, Loader2, Feather } from 'lucide-react';

/**
 * StoryPostcard - renders a print-ready postcard preview of a story
 * with the illustration on top and the story text below.
 * Provides a download button that exports a PNG via html2canvas.
 */
const StoryPostcard = ({ story, open, onOpenChange }) => {
  const cardRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: '#fef3c7',
        scale: 2, // 2x for print-quality
        useCORS: true,
        logging: false,
      });
      const link = document.createElement('a');
      const safeName = (story.title || 'story').replace(/[^a-z0-9]+/gi, '_').toLowerCase();
      link.download = `${safeName}_postcard.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Postcard export failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  if (!story) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="postcard-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-amber-950">Story Postcard</DialogTitle>
          <DialogDescription className="text-sm text-amber-800/70">
            Preview and download a keepsake image of your story.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4">
          {/* The card that gets exported */}
          <div
            ref={cardRef}
            className="mx-auto bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 p-8 border border-amber-200 rounded-lg"
            style={{ width: 560, fontFamily: 'Instrument Sans, sans-serif' }}
            data-testid="postcard-preview"
          >
            {/* Header with brand */}
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-amber-200">
              <div className="flex items-center space-x-2">
                <div className="bg-amber-100 p-1.5 rounded-full">
                  <Feather className="h-4 w-4 text-amber-800" />
                </div>
                <span
                  style={{ fontFamily: 'Fraunces, Georgia, serif', letterSpacing: '-0.02em' }}
                  className="text-lg font-bold text-amber-950"
                >
                  Flash Fiction
                </span>
              </div>
              {story.category && (
                <span className="text-xs uppercase tracking-wider text-amber-700 bg-amber-100 px-2 py-1 rounded-full capitalize">
                  {story.category}
                </span>
              )}
            </div>

            {/* Illustration */}
            {story.illustration && (
              <img
                src={story.illustration}
                alt=""
                className="w-full aspect-square object-cover rounded-lg mb-5 ring-1 ring-amber-200"
                crossOrigin="anonymous"
              />
            )}

            {/* Title */}
            <h2
              style={{ fontFamily: 'Fraunces, Georgia, serif', letterSpacing: '-0.03em' }}
              className="text-3xl font-bold text-amber-950 leading-tight mb-2"
            >
              {story.title}
            </h2>

            {/* Prompt */}
            {story.prompt && (
              <p
                style={{ fontFamily: 'Instrument Serif, Georgia, serif', fontStyle: 'italic' }}
                className="text-sm text-amber-700 mb-4"
              >
                &ldquo;{story.prompt}&rdquo;
              </p>
            )}

            {/* Story text */}
            <p
              style={{ fontFamily: 'Instrument Serif, Georgia, serif' }}
              className="text-gray-800 leading-relaxed whitespace-pre-wrap text-[15px]"
            >
              {story.content}
            </p>

            {/* Footer */}
            <div className="mt-6 pt-4 border-t border-amber-200 flex justify-between items-center text-xs text-amber-800/70">
              <span>{story.word_count} words</span>
              <span
                style={{ fontFamily: 'Fraunces, Georgia, serif' }}
                className="italic"
              >
                flashfiction
              </span>
            </div>
          </div>

          <div className="mt-6 flex justify-end space-x-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="postcard-close-button">
              Close
            </Button>
            <Button
              onClick={handleDownload}
              disabled={downloading}
              className="bg-amber-900 hover:bg-amber-800 text-amber-50"
              data-testid="postcard-download-button"
            >
              {downloading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Preparing&hellip;
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Download PNG
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StoryPostcard;
